import { useEffect, useRef, useState } from 'react';
import { bankAccountsAPI, borrowersAPI } from 'services/swagger-api';
import { BorrowerQuoteTabDto } from 'api/quote';

interface UseCoBorrowerBankAccountPrefillProps {
  /** Devis du co-emprunteur en cours de saisie. */
  quoteId: string;
  /** Personne du co-emprunteur (cible de la copie). */
  personId?: string;
  /** Onglets emprunteurs du projet, le premier est l'emprunteur principal. */
  quoteTabs?: BorrowerQuoteTabDto[] | null;
  /** Compte déjà sélectionné par le co-emprunteur (jamais écrasé). */
  selectedBankAccountId?: string;
  /** Reçoit l'id du compte copié à sélectionner. */
  onPrefilled: (bankAccountId: string) => void;
  /** Risque assuré pour le flux d'activité (optionnel). */
  risqueAssureId?: string;
}

interface UseCoBorrowerBankAccountPrefillReturn {
  /** Nom de l'emprunteur principal quand un pré-remplissage a eu lieu, sinon undefined. */
  prefilledFromName?: string;
  /** Incrémenté après un pré-remplissage pour que la liste des comptes se recharge. */
  refreshKey: number;
}

/**
 * Deux emprunteurs : à l'ouverture des conditions particulières du second, si
 * celui-ci n'a ni compte bancaire ni sélection, on copie le compte sélectionné
 * par le premier emprunteur sur sa personne (endpoint copier, idempotent) et on
 * le sélectionne. Le compte copié lui appartient : le modifier ne touche pas le
 * premier emprunteur. Une seule tentative par montage.
 */
export const useCoBorrowerBankAccountPrefill = ({
  quoteId,
  personId,
  quoteTabs,
  selectedBankAccountId,
  onPrefilled,
  risqueAssureId
}: UseCoBorrowerBankAccountPrefillProps): UseCoBorrowerBankAccountPrefillReturn => {
  const [prefilledFromName, setPrefilledFromName] = useState<string>();
  const [refreshKey, setRefreshKey] = useState(0);
  const attemptedRef = useRef(false);

  useEffect(() => {
    if (attemptedRef.current || !personId || !quoteId || selectedBankAccountId) return;
    const firstTab = quoteTabs?.[0];
    if (!firstTab || firstTab.quoteId === quoteId) return; // emprunteur principal : rien à pré-remplir
    attemptedRef.current = true;

    let cancelled = false;
    (async () => {
      try {
        const own = await bankAccountsAPI.getBankAccountsByPersonId({ personId });
        if (cancelled || (own.bankAccounts?.length ?? 0) > 0) return; // saisie existante : on ne touche à rien
        const first = await borrowersAPI.getSpecialConditionsByQuoteId({
          quoteId: firstTab.quoteId
        });
        if (cancelled || !first?.bankAccountId) return;
        const result = await bankAccountsAPI.copyBankAccount({
          compteBancaireId: first.bankAccountId,
          copyBankAccountCommand: { targetPersonId: personId },
          risqueAssureId
        });
        if (cancelled || !result.isSuccess || !result.id) return;
        onPrefilled(result.id);
        setPrefilledFromName(firstTab.borrowerName || firstTab.label);
        setRefreshKey(k => k + 1);
      } catch (error) {
        console.error('Co-borrower bank account prefill failed:', error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [quoteId, personId, quoteTabs, selectedBankAccountId, onPrefilled, risqueAssureId]);

  return { prefilledFromName, refreshKey };
};
